// ── Module catalog — ONE derived registry for the pictorial per-module marketing pages ─────────
// Powers /modules (ModulesHub) and /modules/:key (ModulePage). DERIVED, never authored: every
// capability entry comes from
//   • parts2.js OFFERING_TABS      — the group (tab) + each feature row [Icon, name, desc, badge]
//   • featureContent.js            — the grounded detail ({what, capabilities, ai}) keyed by name
// Each row carries a semantic Lucide glyph while the marketing surfaces resolve its project-owned,
// feature-specific 3D asset through components/marketing/module3dAssets.js. Product UI continues to
// use Lucide directly. NO capability copy is invented here — one-liners, detail paragraphs and
// bullets stay aligned with the authored product-content registries.
import {
  BarChart3, BookOpen, Brain, Building2, FileSpreadsheet, Globe, GraduationCap, IndianRupee, Megaphone,
  MessageCircle, Mic, PenLine, Settings, ShieldCheck, Users, UsersRound, Workflow,
} from "lucide-react";
import { OFFERING_TABS, publicFeatureBadge } from "./parts2";
import { FEATURE_CONTENT } from "./featureContent";
import { moduleProfileFor } from "./moduleProfiles";

/** Stable URL slug for a feature/module name ("Fee Collection" → "fee-collection"). */
export const slugFor = (name) =>
  String(name)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const TOP_LEVEL_MODULE_NAMES = new Set([
  "Timetable Management", "Curriculum Planning", "Lesson Plan Management", "Homework & Assignments",
  "Digital Gradebook", "Exam Scheduling", "Result Publishing", "Student Admission", "Enquiry Management",
  "Staff Management", "Document Management", "Transport Management", "Task Management", "Hostel Management",
  "Inventory Management", "Library Management", "Visitor Management", "Front Office", "Asset Management",
  "Branch Management", "User Management", "Institute Settings", "Fee Structure", "Fee Collection",
  "Expense Management", "Payroll", "Refund Management", "Student Attendance", "Staff Attendance",
  "Leave Management", "Holiday Management", "Complaint Management", "Chat System", "Circular Management",
]);

const kindFor = ({ name, group, badge }) => {
  if (group === "reports" || /\bReports?\b|^Export\b|Charts$|Analytics$|Summary$/.test(name)) return "Report & analytics";
  if (/\bApp$/.test(name)) return "Mobile experience";
  if (badge === "Device") return "Device integration";
  if (["Online Classes", "Online Payment", "Payment Gateway", "SMS Alerts", "WhatsApp Alerts", "Email Notifications", "Push Notifications"].includes(name)) return "Provider integration";
  if (name === "Automations") return "Workflow automation";
  if (group === "aira") return "Governed AI capability";
  return TOP_LEVEL_MODULE_NAMES.has(name) ? "ERP module" : "ERP feature";
};

// NOTE: the Fluent-Emoji 3D art map that used to live here (a public art directory plus a
// `marketingArtFor` resolver) was REMOVED in the dev merge. It had no consumers left — marketing
// pages and operational UI both resolve art through the project-owned registry
// (components/marketing/module3dAssets.js) — and dev's moduleArt.navigation gate forbids production
// source from referencing that retired directory at all, so keeping a dead map alive only re-opened
// the second art system. (The gate greps raw source, so this note deliberately does not spell the
// old path.)

const catalog = [];
const byKey = {};
const groups = [];

for (const tab of OFFERING_TABS) {
  const group = {
    k: tab.k,
    label: tab.label,
    title: tab.title,
    headline: tab.headline,
    desc: tab.desc,
    color: tab.color,
    deep: tab.deep,
    icon: tab.icon,
    modules: [],
  };
  for (const [Icon, name, oneLiner, badge] of tab.features) {
    const key = slugFor(name);
    if (byKey[key]) continue; // names are unique by the parts2↔featureContent contract; guard anyway
    const entry = FEATURE_CONTENT[name] || null;
    const mod = {
      key,
      name,
      badge: badge || "Core",
      publicStatus: publicFeatureBadge(badge),
      Icon,
      group: tab.k,
      groupLabel: tab.label,
      groupTitle: tab.title,
      groupColor: tab.color,
      groupDeep: tab.deep,
      GroupIcon: tab.icon,
      oneLiner,
      detail: entry ? { what: entry.what, bullets: entry.capabilities || [] } : null,
      aiFeature: !!entry?.ai,
      kind: kindFor({ name, group: tab.k, badge }),
      roles: moduleProfileFor(key)?.roles || [],
    };
    catalog.push(mod);
    byKey[key] = mod;
    group.modules.push(mod);
  }
  groups.push(group);
}

// ── The seventh group — "Aira AI" (authored HERE, not derived from OFFERING_TABS) ───────────────
// The landing #modules explorer keeps its 6 operational tabs; the flagship AI surfaces get their
// own purple group on /modules + /modules/:key (2026-08-03 — they previously had no pages at all).
// Copy is LIFTED (condensed, never invented) from the approved AI marketing sources — the
// AiSuiteSection.js persona cards, featureContent.js, and docs/marketing/MARKETING_CONTENT.md §4 —
// under the CLAIMS_REGISTRY rows A0–A10 / R1 / S7. Colors are the `ai` token pair (#7C3AED /
// dark #6D28D9 — ALL AI UI is purple, never the brand blue). Deterministic Automations is moved to
// Administration below because its entitlement and execution model are separate from Aira.
const AI_GROUP = {
  k: "aira",
  label: "Aira AI",
  title: "Aira AI",
  headline: "Turn School Data Into Better Decisions.",
  desc:
    "Aira works inside fees, compliance, results and the front office — answering from records the asking person is already allowed to see, and proposing actions a person confirms.",
  color: "#7C3AED",
  deep: "#6D28D9",
  icon: Brain,
  modules: [],
};

// [name, Icon, oneLiner, what, capabilities] — every string condensed from the sources above.
const AI_FEATURES = [
  [
    "Aira Assistant", MessageCircle,
    "Ask permission-scoped questions about supported school records by text or voice.",
    "When voice is configured, ask supported questions by text or voice in 11 supported languages and hear the answer read aloud. Aira uses a fixed set of permission-checked tools, so each person can request only the supported records their role is allowed to reach. The default AI router pattern-redacts common identifiers; name removal depends on the calling flow supplying the known values.",
    [
      "Supported answers include student counts, fee outstanding, today's attendance and open tasks",
      "Voice in and read-aloud out when the voice provider is configured",
      "Safe write-actions: Aira proposes, a human confirms, then it executes",
      "Included with the Aira assistant tier · metered as credits",
    ],
  ],
  [
    "Parent Aira", Users,
    "Answers a parent about their own child — from live data, never another family's.",
    "Answers are scoped to the authenticated parent's own children — Aira's tools can only look up student IDs linked to that account. The default router pattern-redacts common identifiers, and known names can be tokenized when the calling flow supplies them. Supported channels include the web portal, the early-access app and an opt-in WhatsApp auto-reply when configured.",
    [
      "Fees, attendance, published marks & the bus — from live data",
      "Web portal + early-access mobile app + configured WhatsApp",
      "Role-scoped tools plus configured identifier safeguards",
      "School-controlled: on/off toggle + a protected credit budget",
    ],
  ],
  [
    "Voice Aira", Mic,
    "Speak in 11 languages, dictate clean requests and turn consented school conversations into reviewable notes.",
    "When the Sarvam voice provider is configured, speak a question in any of 11 supported languages and hear the answer read back. Dictation can remove fillers while keeping meaning, and the separately entitled Meeting Notes workflow turns consented conversations into a structured draft that a person reviews before sharing.",
    [
      "Automatic language detection across English + 10 Indian languages",
      "Speech-to-clean-request refinement with the original wording recoverable",
      "Consent-gated meeting capture with audio discarded after transcription",
      "Structured notes, action items and approval before sharing",
    ],
  ],
  [
    "Migration Copilot", FileSpreadsheet,
    "Map, validate, preview and import student CSV or XLSX files.",
    "Upload a student CSV or XLSX file, review the suggested column map, correct it when needed, and preview row-level validation before commit. The current Copilot commit path imports student records only; it does not claim staff, fee-demand or accounting migration. Imported records remain under onboarding quiet-mode controls while the school verifies the batch.",
    [
      "Import student records from CSV or XLSX through one governed workflow",
      "Review suggested mappings and row-level validation before commit",
      "Use quiet mode and batch rollback while the school verifies imported records",
      "The mapping helper is limited to the metadata supplied by the import workflow",
    ],
  ],
  [
    "Compliance AI", ShieldCheck,
    "Pre-submission DPDP, UDISE+ and APAAR scans with plain-language fixes.",
    "A deterministic rulebook scans eligible records for UDISE+, APAAR and DPDP gaps — a missing DOB, guardian mobile or PEN, or a consent gap — and rolls them into school-wide readiness scores. For a supported explain request, the AI receives the rule and aggregate count rather than the student row, then returns reviewable fix steps.",
    [
      "UDISE+ / APAAR readiness % at a glance",
      "Why-it-matters + fix steps, with the rule cited",
      "Consent-gap and format checks before the deadline",
      "The scan is deterministic & free · AI explain uses Aira credits",
    ],
  ],
  [
    "Report Card AI", PenLine,
    "Board-aware remarks drafted from each student's real marks — teachers review, not type.",
    "The agent reads the permitted marks and attendance inputs plus your school's board configuration — CBSE 8-point, ICSE, state boards or the NEP Holistic Progress Card — and drafts a remark for teacher review. Configured identifier checks run on the request path; the class teacher reviews and signs off before publication.",
    [
      "Board-aware remarks + strengths & focus areas",
      "Whole-class batch drafting in one click",
      "Optional translation into the parent's language",
      "AI drafts, the teacher approves — nothing auto-publishes",
    ],
  ],
  [
    "Website Builder AI", Globe,
    "Draft and edit a multi-page school website through supported instructions.",
    "Draft a multi-page marketing site from a brief, edit it through supported natural-language instructions, and prepare section copy and SEO for human review. Lead-capture blocks can hand admissions, contact and event enquiries to the CRM, while the optional public bot is limited to approved published content rather than student or fee tools.",
    [
      "A multi-page site draft from a structured brief",
      "Edit through supported instructions; review section copy and SEO",
      "Education-scope guardrails on every AI action",
      "Enabled lead-capture blocks hand enquiries to the admissions CRM",
    ],
  ],
  [
    "Talk to Your Data", BarChart3,
    "Plain-English answers from live school data — and the AI never writes SQL.",
    "Your question is routed to one report in a fixed, vetted catalogue — the AI never writes a database query. The report runs scoped to your school, then the AI narrates the result over a digest with every name and contact column stripped out. The full table, names included, renders only in your browser — it never reaches the model.",
    [
      "Plain-English questions over the vetted report catalogue",
      "Every figure computed by the report engine, not the AI",
      "Amounts always in real ₹ — never approximations",
      "The full table renders only in your browser — it never reaches the model",
    ],
  ],
  [
    "Automations", Workflow,
    "40 ready-made rules and a visual builder — every run audited.",
    "When Automation is included in your plan and switched on, the library contains 40 starter rules for fee and invoice reminders, absence WhatsApps, attendance-shortfall alerts, at-risk interventions and document-expiry sweeps. Rules start disabled. Build or edit a rule on the visual canvas and simulate it before enabling; side-effects come only from a fixed catalogue of reviewed actions.",
    [
      "40 starter rules, off by default — you choose what runs",
      "No-code if-this-then-that with nested branching; simulate before enabling",
      "Version history with one-click rollback",
      "Per-run, per-step audit drill-down — no open code execution",
    ],
  ],
];

for (const [name, Icon, oneLiner, what, capabilities] of AI_FEATURES) {
  const key = slugFor(name);
  if (byKey[key]) continue;
  // The deterministic rule builder has a separate Automation entitlement and is not an Aira
  // assistant. Keep it in Administration instead of presenting it as an AI capability.
  const isAutomation = name === "Automations";
  const targetGroup = isAutomation ? groups.find((group) => group.k === "admin") : AI_GROUP;
  const mod = {
    key,
    name,
    badge: isAutomation ? "Smart" : "AI",
    publicStatus: isAutomation ? "Plan feature" : "AI-assisted",
    Icon,
    group: targetGroup.k,
    groupLabel: targetGroup.label,
    groupTitle: targetGroup.title,
    groupColor: targetGroup.color,
    groupDeep: targetGroup.deep,
    GroupIcon: targetGroup.icon,
    oneLiner,
    detail: { what, bullets: capabilities },
    aiFeature: !isAutomation,
    kind: kindFor({ name, group: targetGroup.k, badge: isAutomation ? "Smart" : "AI" }),
    roles: moduleProfileFor(key)?.roles || [],
  };
  catalog.push(mod);
  byKey[key] = mod;
  targetGroup.modules.push(mod);
}

groups.push(AI_GROUP); // /modules renders groups in order → Aira AI lands after Communication

// ── Buyer-facing workspace taxonomy ───────────────────────────────────────────────────────────
// OFFERING_TABS remains the authored source catalogue and its seven legacy groups remain stable for
// product code and existing routes. Marketing discovery uses the nine workspaces below so a school
// owner sees the same operating model as the application navigation. A capability has one primary
// workspace for browse/filter purposes; connected neighbours remain available through its profile.
// Aira is deliberately NOT a tenth workspace. Each governed-AI capability has a primary operational
// home and `crossWorkspaceLayer: "aira"`, allowing /modules to present Aira as an intelligence layer.
const WORKSPACE_DEFINITIONS = [
  {
    k: "leadership-analytics",
    label: "Leadership & Analytics",
    title: "Leadership & Analytics",
    headline: "See performance, exceptions and decisions in one view.",
    desc: "Bring current academic, operational and financial signals into dashboards and reports that school leaders can review and act on.",
    owners: ["School leaders", "Trustees", "MIS teams"],
    color: "#0E7490",
    icon: BarChart3,
  },
  {
    k: "admissions-students",
    label: "Admissions & Students",
    title: "Admissions & Students",
    headline: "Move each enquiry into a complete, usable student record.",
    desc: "Capture interest, enrol students, collect required documents and carry verified records into the school journey.",
    owners: ["Admissions teams", "Front office", "Registrars"],
    color: "#2563EB",
    icon: GraduationCap,
  },
  {
    k: "academics",
    label: "Academics",
    title: "Academics",
    headline: "Plan teaching, record participation and publish outcomes together.",
    desc: "Coordinate timetables, curriculum, attendance, assignments, examinations and report cards without breaking the learning record into separate tools.",
    owners: ["Teachers", "Academic coordinators", "Principals"],
    color: "#1D4ED8",
    icon: BookOpen,
  },
  {
    k: "finance",
    label: "Finance",
    title: "Finance",
    headline: "Keep every charge, collection and accounting output traceable.",
    desc: "Configure fee plans, collect against invoices, apply governed relief, follow up dues and review current finance reports from one record chain.",
    owners: ["Finance teams", "Fee desks", "School leaders"],
    color: "#EA580C",
    icon: IndianRupee,
  },
  {
    k: "hr-payroll",
    label: "HR & Payroll",
    title: "HR & Payroll",
    headline: "Manage staff records, time, leave and payroll with clear ownership.",
    desc: "Connect workforce records with attendance, approved leave, schedules and payroll while keeping role boundaries visible.",
    owners: ["HR teams", "Payroll teams", "School leaders"],
    color: "#0F766E",
    icon: UsersRound,
  },
  {
    k: "campus-operations",
    label: "Campus Operations",
    title: "Campus Operations",
    headline: "Coordinate the physical services that keep each school day running.",
    desc: "Manage transport, hostel, library, inventory, visitors, assets, tasks and compatible attendance devices around accountable school workflows.",
    owners: ["Operations teams", "Administrators", "Service owners"],
    color: "#047857",
    icon: Building2,
  },
  {
    k: "communication-family",
    label: "Communication & Family Engagement",
    title: "Communication & Family Engagement",
    headline: "Turn school events into timely, audience-specific communication.",
    desc: "Coordinate notices, configured channels, moderated conversations and family experiences without disconnecting the message from its school record.",
    owners: ["School offices", "Teachers", "Families"],
    color: "#B91C1C",
    icon: Megaphone,
  },
  {
    k: "automation-compliance",
    label: "Automation & Compliance",
    title: "Automation & Compliance",
    headline: "Standardise repeatable work without giving up human review.",
    desc: "Route approvals, simulate governed automations and review readiness checks while authorised staff remain responsible for consequential actions.",
    owners: ["Process owners", "Compliance teams", "Administrators"],
    color: "#475569",
    icon: Workflow,
  },
  {
    k: "platform-settings",
    label: "Platform & Settings",
    title: "Platform & Settings",
    headline: "Configure access, campuses and data foundations once.",
    desc: "Set up users, roles, school rules, campuses and governed student-data migration before operational teams go live.",
    owners: ["System administrators", "Implementation teams", "School leaders"],
    color: "#334155",
    icon: Settings,
  },
];

// Default from the authored catalogue family, followed by the few entries whose buyer-facing home
// is more specific. Keeping the override table key-based makes every exception reviewable in tests.
const DEFAULT_WORKSPACE_BY_SOURCE_GROUP = {
  academics: "academics",
  admin: "campus-operations",
  fees: "finance",
  attendance: "academics",
  reports: "leadership-analytics",
  comms: "communication-family",
  aira: "leadership-analytics",
};

const WORKSPACE_KEY_OVERRIDES = {
  // Student lifecycle
  "student-admission": "admissions-students",
  "enquiry-management": "admissions-students",
  "id-card-management": "admissions-students",
  "document-management": "admissions-students",
  "certificate-generation": "admissions-students",
  "promotion-management": "admissions-students",

  // People and payroll
  "staff-management": "hr-payroll",
  payroll: "hr-payroll",
  "staff-attendance": "hr-payroll",
  "leave-management": "hr-payroll",
  "staff-shift-tracking": "hr-payroll",

  // Campus services and compatible attendance hardware
  "biometric-attendance": "campus-operations",
  "qr-attendance": "campus-operations",
  "rfid-attendance": "campus-operations",

  // Family communication and public presence
  "front-office": "communication-family",
  "parent-notification": "communication-family",
  "parent-aira": "communication-family",
  "voice-aira": "communication-family",
  "website-builder-ai": "communication-family",

  // Controlled finance follow-up
  "automated-fee-reminders": "finance",

  // Automation and statutory readiness
  "approval-workflow": "automation-compliance",
  "custom-forms": "automation-compliance",
  automations: "automation-compliance",
  "auto-absence-alert": "automation-compliance",
  "compliance-ai": "automation-compliance",

  // Shared platform configuration and onboarding
  "role-and-permission": "platform-settings",
  "branch-management": "platform-settings",
  "user-management": "platform-settings",
  "institute-settings": "platform-settings",
  "migration-copilot": "platform-settings",

  // Aira capabilities with a clear operational home
  "report-card-ai": "academics",
  "aira-assistant": "leadership-analytics",
  "talk-to-your-data": "leadership-analytics",
};

const workspaceDefinitionByKey = Object.fromEntries(WORKSPACE_DEFINITIONS.map((workspace) => [workspace.k, workspace]));

for (const mod of catalog) {
  const workspaceKey = WORKSPACE_KEY_OVERRIDES[mod.key] || DEFAULT_WORKSPACE_BY_SOURCE_GROUP[mod.group];
  const workspace = workspaceDefinitionByKey[workspaceKey];
  if (!workspace) throw new Error(`Missing workspace mapping for module catalogue entry: ${mod.key}`);
  mod.workspace = workspace.k;
  mod.workspaceLabel = workspace.label;
  mod.workspaceTitle = workspace.title;
  mod.workspaceColor = workspace.color;
  mod.WorkspaceIcon = workspace.icon;
  // `aiFeature` also marks AI-assisted operational features in the six authored families. The
  // cross-workspace Aira filter is narrower: only the eight canonical Aira routes authored above.
  mod.crossWorkspaceLayer = mod.group === "aira" ? "aira" : null;
}

/** Nine buyer-facing operating workspaces, each containing a unique slice of the same catalogue. */
export const MODULE_WORKSPACES = WORKSPACE_DEFINITIONS.map((workspace) => ({
  ...workspace,
  modules: catalog.filter((mod) => mod.workspace === workspace.k),
}));

/** Governed Aira capabilities, distributed across their primary operating workspaces. */
export const AIRA_CAPABILITIES = catalog.filter((mod) => mod.crossWorkspaceLayer === "aira");

// Compatibility for links published before the nine-workspace catalogue. Exact new keys win;
// `aira` remains a cross-workspace filter rather than becoming a workspace alias.
export const WORKSPACE_FILTER_ALIASES = Object.freeze({
  overview: "leadership-analytics",
  reports: "leadership-analytics",
  admin: "campus-operations",
  fees: "finance",
  attendance: "academics",
  comms: "communication-family",
  communication: "communication-family",
  operations: "campus-operations",
  settings: "platform-settings",
});

/** Resolve a current workspace key, a legacy group key, `aira`, or `all` for catalogue filtering. */
export function resolveWorkspaceFilter(value) {
  const key = String(value || "all").trim().toLowerCase();
  if (key === "all" || key === "aira") return key;
  if (workspaceDefinitionByKey[key]) return key;
  return WORKSPACE_FILTER_ALIASES[key] || "all";
}

/** Flat list of every module entry (115 as of 2026-08 — 107 operational + 8 dedicated Aira routes). */
export const MODULE_CATALOG = catalog;
/** key → module entry. */
export const moduleByKey = byKey;
/** The OFFERING_TABS groups with their resolved module entries attached. */
export const MODULE_GROUPS = groups;

// ── Plan-tier derivation (module key → entitlement feature id) ─────────────────────────────────
// ONLY modules whose page is unambiguously the marketing face of ONE gateable entitlement id
// (backend core/entitlements.GATEABLE_FEATURES — the same ids the /public/marketing plan_matrix
// serves) are listed. The tier NAME is never stated here: ModulePage resolves it at render time
// from the LIVE plan_matrix (first non-trial plan whose features[fid] is true), so a platform
// entitlement edit re-tiers every page with no deploy. A module that blends core + gated surfaces
// (e.g. Transport Management, Discount & Scholarship) is deliberately ABSENT — it renders no tier
// line at all rather than a guessed one (CLAIMS_REGISTRY P2 discipline).
export const ENTITLEMENT_FOR = {
  // Aira AI group → the `assistant` entitlement when the route represents ONLY that gate.
  "aira-assistant": "assistant",
  "parent-aira": "assistant",
  // Voice Aira intentionally blends assistant-gated spoken Q&A with the separate `meeting_notes`
  // entitlement. Leave it unmapped so ModulePage never claims one tier covers both surfaces.
  "talk-to-your-data": "assistant",
  "compliance-ai": "assistant",
  "report-card-ai": "assistant",
  "website-builder-ai": "assistant",
  "migration-copilot": "migration",
  "automations": "automation",
  "fee-reminder": "automation", // the AI dunning ladder is premium-gated on `automation`
  "performance-insights": "insights",
  "online-classes": "lms",
  "study-material": "lms",
  "biometric-attendance": "biometric",
  // Pure HR surfaces are implemented behind the release-controlled `hr_payroll` entitlement.
  // Mixed surfaces such as Teacher App and Staff Reports stay unmapped because they also contain
  // independently available academic/reporting capabilities.
  "staff-management": "hr_payroll",
  "payroll": "hr_payroll",
  "staff-attendance": "hr_payroll",
  "leave-management": "hr_payroll",
  "staff-shift-tracking": "hr_payroll",
  "branch-management": "group_chain",
  "certificate-generation": "certificates",
  "accounting-reports": "accounting",
  "hostel-management": "hostel",
  "hostel-fees": "hostel",
  "library-management": "library",
  "library-reports": "library",
  "front-office": "frontoffice",
  "visitor-management": "frontoffice",
  "inventory-management": "inventory",
  "asset-management": "inventory",
  // The report-catalogue family (featureContent grounds each in the 13-report catalogue)
  "student-reports": "reports",
  "fee-reports": "reports",
  "attendance-reports": "reports",
  "academic-reports": "reports",
  "exam-reports": "reports",
  "admission-reports": "reports",
  "transport-reports": "reports",
  "custom-reports": "reports",
  "mis-reports": "reports",
};

/** Explicit, evidence-backed workflow neighbours from moduleProfiles; never catalogue adjacency. */
export function relatedModules(mod, n = 4) {
  const profile = moduleProfileFor(mod);
  return (profile?.related || []).map((key) => byKey[key]).filter(Boolean).slice(0, n);
}
